import DataGridSkeleton, {
  DataGridMobileSkeleton,
  PLACEHOLDER_COLUMNS,
} from '@/components/DataGrid/DataGridSkeleton';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useMediaQuery } from '@/hooks/useMediaQuery';

/**
 * Width at which the detail page splits into two columns. The same query
 * `DetailPageContent` uses — the skeleton has to break where the page breaks.
 */
const TWO_COLUMN_QUERY = '(min-width: 1280px)';

/**
 * One overview field, in `FieldRow`'s two shapes: a name-left / value-right row
 * below 768px, the stacked label-over-value cell from 768 up.
 */
const SkeletonField = () => (
  <div className="flex items-center justify-between gap-4 py-2 md-768:block md-768:py-0">
    <div className="h-3 w-20 flex-shrink-0 rounded bg-ds-bg-subtle" />
    <div className="h-4 w-40 rounded bg-ds-bg md-768:mt-2" />
  </div>
);

/** The SectionCard shell: translucent outer panel, 56px header, white body. */
const SkeletonCard = ({
  children,
  headerAction = false,
  bodyClassName = 'p-6',
}: {
  children: React.ReactNode;
  headerAction?: boolean;
  bodyClassName?: string;
}) => (
  <div className="rounded-2xl border border-ds-dark-6 bg-white/50 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
    <div className="flex h-14 items-center justify-between px-5">
      <div className="h-5 w-28 rounded bg-ds-bg" />
      {headerAction && <div className="h-8 w-24 rounded-xl bg-ds-bg-subtle" />}
    </div>
    <div className={`rounded-2xl border border-[rgba(39,39,74,0.0588)] bg-white ${bodyClassName}`}>
      {children}
    </div>
  </div>
);

export interface DetailPageSkeletonProps {
  /** Overview fields in the first card. Mirrors the entity's field count. */
  overviewFields?: number;
  /** Cards below the overview that hold a table. */
  tableCards?: number;
  /** Cards below the overview that hold plain rows (billing, schedule…). */
  listCards?: number;
  /** Render the client and chat cards. */
  showSidebar?: boolean;
  /**
   * Render the Service Provider card above the client one. Off by default, so
   * the generic fallback skeleton keeps the shape it had; the four record pages
   * turn it on through `DETAIL_SKELETON_SHAPES`.
   */
  showProviderCard?: boolean;
  /**
   * Render the Payment Status card above the provider one. The invoice is the
   * only page with it.
   */
  showPaymentCard?: boolean;
}

/**
 * Loading state for a detail page.
 *
 * It mirrors `DetailPageLayout` + `DetailPageContent` + `SectionCard` shape for
 * shape — same paddings, same 56px card headers, same white inner card — so the
 * page does not resize when the data lands. A centred spinner cannot do that:
 * the whole layout snaps into place on load.
 *
 * That means tracking the page at both of its breakpoints, which this did not
 * do and is why it visibly rearranged itself on arrival:
 *
 *  - Order. Stacked, the page reads Payment (invoice only), Overview, Client,
 *    everything else, chat.
 *    The skeleton was concatenating the two columns — Overview, the panels,
 *    then Client and chat at the very bottom — so Client jumped from last to
 *    second the moment the data landed.
 *  - Grid cards. Below 768px `DataGrid` draws a stack of cards, not a table, so
 *    the Services and Products placeholders have to be cards there too. A table
 *    skeleton relaid out into cards on arrival.
 */
export default function DetailPageSkeleton({
  overviewFields = 6,
  tableCards = 1,
  listCards = 1,
  showSidebar = true,
  showProviderCard = false,
  showPaymentCard = false,
}: DetailPageSkeletonProps) {
  const isTwoColumn = useMediaQuery(TWO_COLUMN_QUERY);
  const isMobile = useIsMobile();

  const overviewCard = (
    <SkeletonCard bodyClassName="px-6 py-3 md-768:p-6">
      <div className="grid grid-cols-1 gap-x-10 md-768:grid-cols-2 md-768:gap-6">
        {Array.from({ length: overviewFields }).map((_, index) => (
          <SkeletonField key={index} />
        ))}
      </div>
    </SkeletonCard>
  );

  const gridCards = Array.from({ length: tableCards }).map((_, index) => (
    <SkeletonCard key={`table-${index}`} bodyClassName={isMobile ? 'p-0' : 'overflow-x-auto p-5'}>
      {isMobile ? (
        <DataGridMobileSkeleton rowCount={2} fieldCount={2} />
      ) : (
        <DataGridSkeleton columns={PLACEHOLDER_COLUMNS(4)} rowCount={3} />
      )}
    </SkeletonCard>
  ));

  const rowCards = Array.from({ length: listCards }).map((_, index) => (
    <SkeletonCard key={`list-${index}`}>
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, rowIndex) => (
          <div key={rowIndex} className="flex items-center justify-between">
            <div className="h-4 w-24 rounded bg-ds-bg-subtle" />
            <div className="h-4 w-20 rounded bg-ds-bg" />
          </div>
        ))}
      </div>
    </SkeletonCard>
  ));

  /**
   * The Service Provider card. Same block as the client one minus the second
   * line under the name — the real card has no subtitle there. It arrives from
   * its own query, later than the record, so without this the client card was
   * painted first and then shoved down when the provider landed.
   */
  const providerCard = (
    <SkeletonCard bodyClassName="p-4 sm:p-5">
      <div className="mb-4 flex items-center gap-3">
        <div className="h-10 w-10 flex-shrink-0 rounded-full bg-ds-bg" />
        <div className="min-w-0 flex-1">
          <div className="h-5 w-32 rounded bg-ds-bg" />
        </div>
      </div>
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index}>
            <div className="mb-1 h-4 w-16 rounded bg-ds-bg-subtle" />
            <div className="h-5 w-40 rounded bg-ds-bg" />
          </div>
        ))}
      </div>
    </SkeletonCard>
  );

  /**
   * The invoice's Payment Status card: first in its sidebar, and first on the
   * page once the columns stack — the balance is what the invoice was opened
   * for, so it does not sit below four panels. Its body is a placeholder here
   * and goes on being one inside `PaymentFlow` until the Stripe eligibility
   * query answers, so the two hand over without the card appearing,
   * disappearing or changing height.
   */
  const paymentCard = (
    <SkeletonCard bodyClassName="p-4 sm:p-5">
      <div className="mb-4">
        <div className="mb-1 h-4 w-24 rounded bg-ds-bg-subtle" />
        <div className="h-8 w-32 rounded bg-ds-bg" />
      </div>
      <div className="mb-4">
        <div className="mb-1 h-4 w-20 rounded bg-ds-bg-subtle" />
        <div className="h-6 w-24 rounded bg-ds-bg" />
      </div>
      <div className="h-8 w-full rounded-[10px] bg-ds-bg" />
    </SkeletonCard>
  );

  const clientCard = (
    <SkeletonCard bodyClassName="p-4 sm:p-5">
      <div className="mb-4 flex items-center gap-3">
        <div className="h-10 w-10 flex-shrink-0 rounded-full bg-ds-bg" />
        <div className="min-w-0 flex-1">
          <div className="h-5 w-32 rounded bg-ds-bg" />
          <div className="mt-1 h-4 w-20 rounded bg-ds-bg-subtle" />
        </div>
      </div>
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index}>
            <div className="mb-1 h-4 w-16 rounded bg-ds-bg-subtle" />
            <div className="h-5 w-40 rounded bg-ds-bg" />
          </div>
        ))}
      </div>
    </SkeletonCard>
  );

  // Chat card — the composer keeps it a fixed height in the real page.
  const chatCard = (
    <SkeletonCard bodyClassName="p-0">
      <div className="h-[460px]" />
    </SkeletonCard>
  );

  const header = (
    /* Header — back button, record title, status badge.
       `ds-dark-6`, not `ds-bg`: these three sit on the page gray
       (--ds-bg-secondary #F2F3F5), and #F2F3F5 against it is a single point of
       difference — the title and badge were being drawn and were simply
       invisible, so the skeleton read as cards and nothing else. The bars
       inside the white cards keep the lighter tokens.
       Shapes track the real header: a 36/40px framed back button, a 20/24px
       bold title, and the `lg` status badge beside it. */
    <div className="mb-6 flex flex-col justify-center gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-center gap-3 sm:flex-1 sm:basis-[320px]">
        <div className="h-9 w-9 flex-shrink-0 rounded-xl border border-[rgba(39,39,74,0.0784)] bg-white md-768:h-10 md-768:w-10" />
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <div className="h-7 w-48 rounded bg-ds-dark-6 sm:h-8 sm:w-64" />
          <div className="h-[30px] w-28 flex-shrink-0 rounded-full bg-ds-dark-6" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full bg-ds-bg-secondary" data-testid="detail-page-skeleton">
      <div className="w-full animate-pulse px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
        {header}

        {isTwoColumn ? (
          <div className="flex gap-5 xl:items-start">
            <div className="min-w-0 flex-1 space-y-5">
              {overviewCard}
              {gridCards}
              {rowCards}
            </div>

            {showSidebar && (
              <div className="w-[28%] flex-shrink-0 space-y-5">
                {showPaymentCard && paymentCard}
                {showProviderCard && providerCard}
                {clientCard}
                {chatCard}
              </div>
            )}
          </div>
        ) : (
          // The stacked order the page itself uses: Payment, Overview, Client,
          // everything else, chat last.
          <div className="flex flex-col gap-5">
            {showSidebar && showPaymentCard && paymentCard}
            {overviewCard}
            {showSidebar && showProviderCard && providerCard}
            {showSidebar && clientCard}
            {gridCards}
            {rowCards}
            {showSidebar && chatCard}
          </div>
        )}
      </div>
    </div>
  );
}
